import { evaluateExpression } from "../../Workflow/ExpressionBuilder/utils/expressionBuilder.utils";

/**
 * Flattens form elements including nested row-container grid cells.
 * @param {Array} elements
 * @returns {Array}
 * @author Abhishek Dhillon
 */
export const collectFormElements = (elements) => {
  if (!elements?.length) return [];
  return elements.flatMap((element) => {
    if (!element) return [];
    const nested =
      element.type === "row-container" && element.gridElements
        ? collectFormElements(element.gridElements.flat().filter(Boolean))
        : [];
    return [element, ...nested];
  });
};

/**
 * Resolves smart-chip id used for expression data and onFieldChange sync.
 * @param {Object} element
 * @returns {string|undefined}
 * @author Abhishek Dhillon
 */
export const getSmartChipId = (element) => element?.smartChipId || element?.properties?.workflowVariableId;

/**
 * Whether a form element or subsection should be shown (Show If disabled or expression passes).
 * @param {Object} item - Element or subsection with optional showIf / showIfExpression
 * @param {Object} smartChipResponses
 * @returns {boolean}
 * @author Abhishek Dhillon
 */
export const isShowIfVisible = (item, smartChipResponses) => {
  if (!item?.showIf || !item?.showIfExpression) return true;
  return evaluateExpression(smartChipResponses, item.showIfExpression) !== false;
};

/**
 * Whether a field still has a stored form or smart-chip value worth clearing.
 * @param {string} fieldId
 * @param {string|undefined} smartChipId
 * @param {Object} formResponses
 * @param {Object} smartChipResponses
 * @returns {boolean}
 * @author Abhishek Dhillon
 */
export const hasStoredFieldValue = (fieldId, smartChipId, formResponses, smartChipResponses) => {
  const formValue = formResponses[fieldId];
  const hasFormValue = formValue != null && formValue !== "";
  const scValue = smartChipId ? smartChipResponses[smartChipId] : undefined;
  const hasSmartChipValue = smartChipId != null && scValue != null && scValue !== "";
  return hasFormValue || hasSmartChipValue;
};

/**
 * Builds visibility maps in a single pass (one evaluateExpression call per Show If rule).
 * @param {Array} sections - Subsections for the active form tab
 * @param {Object} smartChipResponses
 * @returns {{ elementVisibility: Object, subsectionVisibility: Object, hiddenFields: Array }}
 * @author Abhishek Dhillon
 */
export const buildShowIfVisibility = (sections, smartChipResponses) => {
  const elementVisibility = {};
  const subsectionVisibility = {};
  const hiddenFields = [];

  (sections || []).forEach((subsection) => {
    const subVisible = isShowIfVisible(subsection, smartChipResponses);
    if (subsection?.id != null) {
      subsectionVisibility[subsection.id] = subVisible;
    }
    if (!subVisible) return;

    collectFormElements(subsection.elements).forEach((element) => {
      const visible = isShowIfVisible(element, smartChipResponses);
      if (element?.id == null) return;

      elementVisibility[element.id] = visible;
      if (!visible) {
        hiddenFields.push({ id: element.id, smartChipId: getSmartChipId(element) });
      }
    });
  });

  return { elementVisibility, subsectionVisibility, hiddenFields };
};
